import React from 'react';
import { Alert, Box, Button, MenuItem, TextField, Typography } from '@mui/material';
import { Instruction, InstructionKind } from './lib/types';
import { useBotOpsActions } from './BotOpsActions';

export const MAX_INSTRUCTION_CHARS = 4000;

export interface ComposerTarget { slug: string; label: string }

let composerSeq = 0;

/**
 * One text box for everything Grant says to a bot: "Instruct instead" on an
 * action row, the Inbox composer, and "Ask a question". On failure the text is
 * kept and the error shown; it only clears after the server accepted it. While
 * it holds text the page holds new snapshots back (see BotOpsActions).
 */
export function InstructComposer({
  targets, kind, relatedActionId = null, kindChoice = false, placeholder, submitLabel = 'Send', autoFocus = false, onSent, onCancel, label,
}: {
  targets: ComposerTarget[];
  kind: InstructionKind;
  relatedActionId?: string | null;
  /** Show an Instruction / Question switch (the Inbox composer). */
  kindChoice?: boolean;
  placeholder?: string;
  submitLabel?: string;
  autoFocus?: boolean;
  onSent?: (i: Instruction) => void;
  onCancel?: () => void;
  label: string;
}) {
  const api = useBotOpsActions();
  const idRef = React.useRef(`composer-${(composerSeq += 1)}`);
  const [text, setText] = React.useState('');
  const [target, setTarget] = React.useState(targets[0] ? targets[0].slug : '');
  const [chosenKind, setChosenKind] = React.useState<InstructionKind>(kind);
  const [priority, setPriority] = React.useState<'P0' | 'P1' | 'P2'>('P1');
  const [sending, setSending] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const { setComposing } = api;
  const dirty = text.trim().length > 0;
  React.useEffect(() => {
    const id = idRef.current;
    setComposing(id, dirty);
    return () => setComposing(id, false);
  }, [dirty, setComposing]);

  // The chosen target must stay valid if the list changes under it.
  React.useEffect(() => {
    if (!targets.some((t) => t.slug === target) && targets[0]) setTarget(targets[0].slug);
  }, [targets, target]);

  if (!api.canWrite) {
    return <Typography variant="caption" color="text.secondary" data-testid="composer-readonly">Only the super admin can send instructions or questions.</Typography>;
  }

  const tooLong = text.length > MAX_INSTRUCTION_CHARS;
  const effectiveKind: InstructionKind = kindChoice ? chosenKind : kind;

  const submit = async () => {
    if (!dirty || tooLong || sending) return;
    setSending(true);
    setError(null);
    const r = await api.send({ targetBotSlug: target, text: text.trim(), kind: effectiveKind, relatedActionId, priority });
    setSending(false);
    if (r.ok) {
      setText('');
      if (onSent) onSent(r.instruction);
    } else {
      setError(r.error);
    }
  };

  return (
    <Box
      component="form"
      aria-label={label}
      onSubmit={(e) => { e.preventDefault(); submit(); }}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        e.stopPropagation();
        if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') { e.preventDefault(); submit(); }
        if (e.key === 'Escape' && onCancel && !dirty) onCancel();
      }}
      sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
    >
      <TextField
        multiline
        minRows={2}
        maxRows={8}
        size="small"
        fullWidth
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={placeholder || (effectiveKind === 'question' ? 'Ask a question…' : 'Write an instruction…')}
        autoFocus={autoFocus}
        disabled={sending}
        error={tooLong}
        inputProps={{ 'aria-label': label, maxLength: MAX_INSTRUCTION_CHARS + 200 }}
        helperText={tooLong ? `${text.length}/${MAX_INSTRUCTION_CHARS}: too long` : dirty ? `${text.length}/${MAX_INSTRUCTION_CHARS} · ⌘/Ctrl+Enter to send` : undefined}
      />
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        {kindChoice && (
          <TextField select size="small" label="Type" value={chosenKind} onChange={(e) => setChosenKind(e.target.value as InstructionKind)} sx={{ minWidth: 130 }}>
            <MenuItem value="instruction">Instruction</MenuItem>
            <MenuItem value="question">Question</MenuItem>
          </TextField>
        )}
        {targets.length > 1 && (
          <TextField select size="small" label="Send to" value={target} onChange={(e) => setTarget(e.target.value)} sx={{ minWidth: 190 }}>
            {targets.map((t) => <MenuItem key={t.slug} value={t.slug}>{t.label}</MenuItem>)}
          </TextField>
        )}
        {effectiveKind !== 'question' && (
          <TextField select size="small" label="Priority" value={priority} onChange={(e) => setPriority(e.target.value as 'P0' | 'P1' | 'P2')} sx={{ minWidth: 90 }}>
            <MenuItem value="P0">P0</MenuItem>
            <MenuItem value="P1">P1</MenuItem>
            <MenuItem value="P2">P2</MenuItem>
          </TextField>
        )}
        <Box sx={{ flex: 1 }} />
        {onCancel && <Button size="small" onClick={onCancel} disabled={sending}>Cancel</Button>}
        <Button type="submit" size="small" variant="contained" disabled={!dirty || tooLong || sending}>{sending ? 'Sending…' : submitLabel}</Button>
      </Box>
      {error && <Alert severity="error" role="alert" sx={{ py: 0 }}>{error} Your text is still here.</Alert>}
    </Box>
  );
}
